import React, {useContext, useEffect, useState} from 'react';
import {Divider} from "antd";
import axios from "../common/axios";
import SummaryCard from "../reports/studentReport/SummaryCard";
import ClassDetailsProgressSummary from "./components/ClassDetailsProgressSummary";
import {
    DescriptionRounded,
    ManageAccountsRounded,
    SettingsRounded, VisibilityOffRounded, VisibilityRounded
} from "@mui/icons-material";
import CircularLoader from "../common/CircularLoader";
import useCurrentShortcut from "../hooks/useCurrentShortcut";
import {useBreadcrumbComponent} from "../common/useBreadcrumbComponent";
import useDynamicBreadcrumb from "../hooks/useDynamicBreadcrumb";
import ClassDetailsWeeklySummary from "./components/ClassDetailsWeeklySummary";
import Button from "../common/Button";
import IconActionButton from "./components/IconActionButton";
import {UserContext} from "../common/UserContext";
import ClassRollDialog from "./components/ClassRollDialog";
import {NavigationContext} from "../common/NavigationContext";
import {useSearchParams} from "wouter";
import {BrandContext} from "../common/BrandContext";
import PageHeader from "../common/PageHeader";

const ClassDetails = ({ classIdParam }) => {
    const [classDetails, setClassDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const { loadShortcutByUrl } = useCurrentShortcut();
    const [isClassRollDialogOpen, setIsClassRollDialogOpen] = useState(false);
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const { isDrawerVisible, setDrawerComponentName, setDrawerVisible, setDrawerLocation } = useContext(NavigationContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const classId = classIdParam;

    const targetBreadcrumbPath = `/web/ui/view/classes/schoolClasses/class/${classId}`;

    useDynamicBreadcrumb({
        label: classDetails?.className,
        path: targetBreadcrumbPath
    });

    const loadClass = (clsId) => {
        setLoading(true);
        axios.get(`/web/ui/myClasses/classDetails/${clsId}`)
            .then(response => {
                if (response) {
                    setClassDetails(response.data);
                }
            })
            .catch(error => {
                console.error("Failed to load class details:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if ((classId && !editClass) || !isDrawerVisible) {
            loadClass(classId);
        }
    }, [classId, editClass, isDrawerVisible]);

    const handleEditClass = () => {
        setSearchParams({
            editClassId: classId
        })
        setDrawerComponentName('EditClassDrawer');
        setDrawerLocation('right');
        setDrawerVisible(true);
    }

    const updateClassWorkedSolutionStatus = async () => {
        try {
            const newShowWorkedSolutions = !classDetails.class?.showWorkedSolutions;

            const updatedClassData = {
                className: classDetails.class?.className,
                calendarYear: classDetails.class?.calendarYear,
                schoolYear: classDetails.class?.schoolYear,
                hideWorkspaces: classDetails.class?.hideWorkspaces,
                markingType: classDetails.class?.markingType,
                showSolutions: classDetails.class?.showSolutions,
                showWorkedSolutions: newShowWorkedSolutions
            };

            await axios.post(
                `/web/ui/sc/updateClass?classId=${classId}`,
                updatedClassData,
                { headers: { 'Content-Type': 'application/json' } }
            );

            loadClass(classId);

        } catch (error) {
            console.error('Error updating worked solution status:', error);
        }
    };

    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: true });

    if(loading){
        return(
            <CircularLoader pageCentered={true}/>
        )
    } else {
        return (
            <div className={'ch-class-details'}>
                <BreadcrumbBar />
                <PageHeader
                    title={classDetails.class?.className}
                    buttons={
                        <>
                            <Button
                                type={'outline'}
                                icon={<ManageAccountsRounded />}
                                onClick={() => loadShortcutByUrl(`/web/ui/view/classes/schoolClasses/class/${classId}/classDetails`)}
                            >
                                Manage students
                            </Button>
                            {user.teacher && user.schoolAdmin &&
                                <Button
                                    type={'outline'}
                                    icon={<SettingsRounded />}
                                    onClick={handleEditClass}
                                >
                                    Class settings
                                </Button>
                            }
                            <Button
                                type={'outline'}
                                icon={<DescriptionRounded />}
                                onClick={() => setIsClassRollDialogOpen(true)}
                            >
                                Class roll
                            </Button>
                        </>
                    }
                />
                <Divider style={{margin: "12px 0"}} />
                <div className={'ch-class-details-summary-wrapper'}>
                    <SummaryCard
                        topSummary={[
                            { value: classDetails.students?.length, label: 'Students' },
                            { value: classDetails.teachers?.length, label: 'Teachers' },
                            ...(classDetails.class?.schoolYearDescription ? [
                                { value: classDetails.class.schoolYearDescription, label: 'School year' }
                            ] : [])
                        ]}
                        bottomSummary={[
                            { value: classDetails.class?.classCode, label: 'Class key' },
                            ...(brandConfig.brandCode === 'SENIOR_MATHS_INTEGRATED' ? [
                                { value: classDetails.class?.showWorkedSolutions ? 'Visible' : 'Hidden', label: 'Worked solutions' }
                            ] : [])
                        ]}
                    />
                    <div className={'ch-class-details-button-wrapper'}>
                        <div>
                            <IconActionButton
                                iconName={'reports'}
                                onClick={() => loadShortcutByUrl(`/web/ui/view/reports/activityReport/${classId}`)}
                            >
                                Class activity report
                            </IconActionButton>
                        </div>
                        {brandConfig.brandCode === 'SENIOR_MATHS_INTEGRATED' &&
                            <div>
                                <IconActionButton
                                    muiIcon={
                                    classDetails.class?.showWorkedSolutions
                                    ? <VisibilityOffRounded sx={{ fontSize: 44, color: 'var(--primary-colour)' }} />
                                    : <VisibilityRounded sx={{ fontSize: 44, color: 'var(--primary-colour)' }} />
                                }
                                    onClick={() => updateClassWorkedSolutionStatus()}
                                    data-gtm-value="class-view-fully-worked-solutions"
                                >
                                    {classDetails.class?.showWorkedSolutions ? 'Hide worked solutions' : 'Show worked solutions'}
                                </IconActionButton>
                            </div>
                        }
                    </div>
                    <ClassDetailsWeeklySummary classId={classId} />
                </div>
                <Divider style={{margin: "12px 0"}} />
                <h3>Activity summary</h3>
                <ClassDetailsProgressSummary classId={classId} className={classDetails.class?.className} classStudents={classDetails.students} />
                <ClassRollDialog
                    isOpen={isClassRollDialogOpen}
                    onClose={() => setIsClassRollDialogOpen(false)}
                    selectedClass={classDetails?.class}
                />
            </div>
        );
    }
};

export default ClassDetails;